import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router';
import { datadogRum } from '@datadog/browser-rum';
import { isEmpty } from 'lodash';

import { getInstance as getDSInstance } from 'modules/api/digitalServices/instance';
import { fetchAllBooks, fetchDraftsByBookId, fetchProfile, getPayload } from 'modules/api';
import { formatBookList } from 'modules/v2/pages/HomePage/sections/Books/utils';

import {
  DesignHuddleCategory,
  DesignHuddleContextType,
  DesignHuddleRawCategory,
  ITemplateDynamicInfo,
  ITemplateBuilderProjectDocument,
  TokenPayload,
  UserProfile,
} from './types';
import {
  createTemplateCustomizationObject,
  checkForTokenExpired,
  configureDesignHuddleLib,
  persistToken,
  getStoredToken
} from './utils';

// Constants
const TOKEN_STORAGE_KEY = 'dh_token';

const DesignHuddleContext = createContext<DesignHuddleContextType | undefined>(undefined);

export const DesignHuddleProvider = ({ children }: { children: ReactNode }) => {
  const DSInstance = getDSInstance();
  const location = useLocation();

  // User & Auth State
  const [userProfile, setUserProfile] = useState<UserProfile>(null);
  const [tokenPayload, setTokenPayload] = useState<TokenPayload | null>(null);
  const [userAccessToken, setUserAccessToken] = useState('');
  const [userCustomizationsHash, setUserCustomizationsHash] = useState('');

  // Categories State
  const [searchCategories, setSearchCategories] = useState<DesignHuddleCategory[]>(null);
  const [selectedCategories, setSelectedCategories] = useState({ primary: null, secondary: [] });

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTemplates, setTotalTemplates] = useState(0);
  const ITEMS_PER_PAGE = 50;

  // Data State
  const [dhTemplates, setDhTemplates] = useState<any[]>([]);
  const [dhProjects, setDhProjects] = useState<ITemplateBuilderProjectDocument[]>([]);

  const [legacyDhProjects, setLegacyDhProjects] = useState<[]>([]);

  const [bookList, setBookList] = useState<any[]>([]);
  const [templateDynamicInfo, setTemplateDynamicInfo] = useState<ITemplateDynamicInfo>(null);

  // Loading States
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [isLoadingDynamicInfo, setIsLoadingDynamicInfo] = useState(true);
  const [isLoadingBooks, setIsLoadingBooks] = useState(true);

  // Configuration States
  const [isConfigured, setIsConfigured] = useState(false);
  const [isTokenExpired, setIsTokenExpired] = useState(false);
  const [isExportReady, setIsExportReady] = useState(false);
  const [templateIsConfigured, setTemplateIsConfigured] = useState(false);

  // Request management
  const currentRequestRef = useRef<number>(0);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced setter for templates to prevent rapid updates
  const setDhTemplatesDebounced = useCallback((templates: any[]) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    debounceTimeoutRef.current = setTimeout(() => {
      setDhTemplates(templates);
    }, 150); // 150ms debounce
  }, []);

  // Computed Values
  const isTemplatesPage = useMemo(() => {
    return (location.pathname.includes('/templates/gallery/') ||
           location.pathname.includes('/dashboard/templates/') ||
           location.pathname === '/templates/') && !location.pathname.includes('setup-defaults');
  }, [location.pathname]);

  // Helper Functions
  const mapSearchCategories = useCallback((rawCategories: DesignHuddleRawCategory[]): any => {
    const formatted = {
      primary: {},
      secondary: []
    };

    for (const category of rawCategories) {
      const formattedItems = category.template_category_items.map((item) => ({
        id: item.template_category_item_id,
        name: item.item_name
      }));

      if (category.primary_template_category) {
        formatted.primary = {
          id: category.template_category_id,
          name: category.category_name,
          selectedItemId: null,
          items: formattedItems
        };
      } else {
        const isSocialMediaTemplates = category.template_category_id === 4;
        // Reminder: That's because we want always the most recent month to be shown first
        const resolvedItems = isSocialMediaTemplates ? formattedItems.reverse() : formattedItems;

        formatted.secondary.push({
          id: category.template_category_id,
          name: category.category_name,
          expanded: false,
          selectAll: false,
          selectedItemIds: [],
          items: resolvedItems
        });
      }
    }

    return formatted;
  }, []);

  const getSelectedCategoryParams = useCallback((categories) => {

    if (isEmpty(categories)) return {};

    const params = {};
    
    // Add primary category if exists (check for null/undefined, not 0)
    if (categories?.primary !== null && categories?.primary !== undefined) {
      params.primary_template_category_item_id = categories.primary;
    }

    // Add secondary categories if they exist
    if (categories.secondary && Array.isArray(categories.secondary)) {
      categories.secondary.forEach((cat) => {
        if (cat.selectedItemIds && cat.selectedItemIds.length > 0) {
          params[`category_${cat.id}`] = cat.selectedItemIds;
        }
      });
    }

    return params;
  }, []);

  // API Calls
  const fetchUserProfile = useCallback(async (authPayload: TokenPayload) => {
    try {
      const payload = await fetchProfile();
      const profile = getPayload(payload);

      const resolvedProfile = {
        ...profile,
        imageUrl: profile.afy_customer_profile_image_url,
      };

      const isInternalEmail = Boolean(
        profile.email.includes('@authorify') &&
        authPayload.email.includes('internal-user') &&
        authPayload.email.includes('@authorify')
      );

      if (profile.email !== authPayload.email && !isInternalEmail) {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        window.location.reload();
      }

      setUserProfile(resolvedProfile);
      return resolvedProfile;
    } catch (err) {
      console.error('Error fetching profile:', err);
      throw err;
    }
  }, []);

  const storeCustomizationObject = useCallback((authPayload: TokenPayload, profile: UserProfile, dynamicInfo: ITemplateDynamicInfo) => {
    if (!profile) {
      console.warn('Cannot store customization object: missing profile');
      return;
    }

    const templateCustomizationObject = createTemplateCustomizationObject(profile, dynamicInfo || {});
    configureDesignHuddleLib(authPayload.accessToken);

    window.DSHDLib.storeTemplateCustomizationObject({
      object: templateCustomizationObject,
      save_as_user_default: true,
    }, (err, data) => {
      if (err) {
        setIsConfigured(false);
        return;
      }

      setUserCustomizationsHash(data.object_hash);
      setIsConfigured(true);
    });
  }, []);

  const enhanceTemplateWithPreviewUrl = useCallback((template) => {
    return new Promise(resolve => {
      window.DSHDLib.getVariableTemplatePreviewURL({
        template_id: template.template_id,
        customizations_hash: userCustomizationsHash
      }, (err, url) => {
        if (!err) {
          fetch(url)
            .then((resp) => {
              if (resp.status === 200) {
                template.smartThumbnailUrl = url;
              }
              resolve(template);
            })
            .catch(() => resolve(template));
        } else {
          resolve(template);
        }
      });
    });
  }, [userCustomizationsHash]);

  const fetchBooks = useCallback(async () => {
    try {
      const allBooksResponse = await fetchAllBooks({ isAllowed: true });
      const books = (
        allBooksResponse.data.data.books ||
        allBooksResponse.data.books ||
        []
      ).filter(Boolean);

      const ownedBooks = formatBookList(books).filter((book) => {
        const ownedBookStatuses = ['draft', 'modified', 'done', 'ready'];
        return ownedBookStatuses.includes(book?.status);
      });

      const booksPublishedOrEdited = (await Promise.all(
        ownedBooks.map((book) => fetchDraftsByBookId({ bookId: book.id }))
      )).flatMap(({ data: { data: { results } } }) => results);

      setIsLoadingBooks(false);
      setBookList(booksPublishedOrEdited);
    } catch (error) {
      console.error('[fetchAllBooks] fetch error', error);
      setIsLoadingBooks(false);
    }
  }, []);

  const fetchTemplateCategories = useCallback(async () => {
    try {
      const { data: { data: { items: currentCategories } } } = await DSInstance.get('/dh/categories');
      const mappedCategories = mapSearchCategories(currentCategories);
      setSearchCategories(mappedCategories);
    } catch (error) {
      console.error('[fetchTemplateCategories] fetch error', error);
    }
  }, [DSInstance, mapSearchCategories]);

  const fetchTemplateDynamicInfo = useCallback(async () => {
    try {
      setIsLoadingDynamicInfo(true);
      const { data: currentUserData } = await DSInstance.get('/template-builder');
      setTemplateDynamicInfo(currentUserData);
      setIsLoadingDynamicInfo(false);
    } catch (error) {
      console.error('[fetchTemplateDynamicInfo] fetch error', error);
      setIsLoadingDynamicInfo(false);
      setTemplateDynamicInfo({} as any);
    }
  }, [DSInstance]);

  const fetchTemplates = useCallback((page = currentPage) => {
    if (!isConfigured || !isTemplatesPage) return;

    // Generate unique request ID
    const requestId = ++currentRequestRef.current;
    
    setIsLoadingTemplates(true);
    setDhTemplates([]); // Clear previous templates to avoid showing unrelated content

    const handleTemplatesResponse = async (err, data) => {
      // Check if this is still the most recent request
      if (requestId !== currentRequestRef.current) {
        return;
      }

      if (err) {
        setIsLoadingTemplates(false);
        return;
      }

      const templates = data.items || [];
      const templatesWithUrls = await Promise.all(
        templates.map(enhanceTemplateWithPreviewUrl)
      );

      // Double-check the request is still current before setting state
      if (requestId === currentRequestRef.current) {
        setDhTemplatesDebounced(templatesWithUrls);
        
        // Update pagination info
        if (data.total !== undefined) {
          setTotalTemplates(data.total);
          setTotalPages(Math.ceil(data.total / ITEMS_PER_PAGE));
        }
        
        setIsLoadingTemplates(false);
      }
    };

    const params = getSelectedCategoryParams(selectedCategories);

    window.DSHDLib.getTemplates({
      page: page,
      limit: ITEMS_PER_PAGE,
      ...params,
    }, {}, handleTemplatesResponse);
  }, [isConfigured, isTemplatesPage, enhanceTemplateWithPreviewUrl, selectedCategories, getSelectedCategoryParams, currentPage, setDhTemplatesDebounced]);

  const fetchProjects = useCallback(async () => {
    if (!isConfigured) return;

    const legacyFetch = async () => {
      const handleProjectsResponse = (err, data) => {
        if (!err && data?.items) {
          setLegacyDhProjects(data.items);
        }
      };

      window.DSHDLib.getProjects({ limit: 1000 }, handleProjectsResponse);
    };

    try {
      await legacyFetch();

      const { data } = await DSInstance.get('/template-builder/projects');
      setDhProjects(data || []);
      setIsLoadingProjects(false);
    } catch (error) {
      console.error('[fetchProjects] fetch error', error);
      setDhProjects([]);
      setIsLoadingProjects(false);
    }
  }, [isConfigured, DSInstance]);

  const refreshToken = useCallback(async () => {
    try {
      const api = getDSInstance();
      const response = await api.get('/dh/auth/user');

      if (!response.data?.access_token || !response.data?.expires_in || !response.data?.email) {
        throw new Error('Invalid Design Huddle response');
      }

      const newToken = persistToken(response.data.access_token, response.data.expires_in, response.data.email);
      setTokenPayload(newToken);
      setIsTokenExpired(false);
    } catch (err) {
      setTokenPayload(null);
      setIsTokenExpired(true);
    }
  }, []);

  const initializeDesignHuddle = useCallback(async (authPayload: TokenPayload) => {
    setIsLoadingTemplates(true);

    if (typeof window === 'undefined' || !window.DSHDLib) {
      setIsConfigured(false);
      return;
    }

    setUserAccessToken(authPayload.accessToken);
    
    try {
      await fetchUserProfile(authPayload);
    } catch (err) {
      console.error('Failed to fetch user profile:', err);
    }
  }, [fetchUserProfile]);

  // Action Handlers
  const handlePageChange = useCallback((newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  }, [totalPages]);

  const handleSelectPrimaryCategory = useCallback((itemId) => {
    // Track primary category selection in Datadog
    datadogRum.addAction('primary_category_selected', {
      category_id: itemId,
      category_name: searchCategories?.primary?.find(cat => cat.id === itemId)?.name || itemId
    });

    setSelectedCategories((prev) => {
      if (itemId === 'all') {
        return { ...prev, primary: null };
      }
      return { ...prev, primary: itemId };
    });
    setCurrentPage(1); // Reset to first page when changing category
  }, [searchCategories]);

  const handleSelectSecondaryCategory = useCallback((category: any, itemId: any) => {
    // Track secondary category selection in Datadog
    const secondaryCategory = searchCategories?.secondary?.find(cat => cat.id === category?.id);
    const selectedItem = secondaryCategory?.items?.find(item => item.id === itemId);

    datadogRum.addAction('secondary_category_selected', {
      primary_category_id: category?.id,
      primary_category_name: category?.name || 'unknown',
      secondary_category_id: itemId,
      secondary_category_name: selectedItem?.name || itemId
    });

    setSelectedCategories({
      ...selectedCategories,
      secondary: [
        // ...selectedCategories.secondary,
        {
          id: category?.id,
          selectedItemIds: [itemId]
        }
      ]
    });

    setCurrentPage(1);

  }, [searchCategories, selectedCategories]);

  const toggleAllSecondaryCategories = useCallback((category: any) => {
    if (!searchCategories?.secondary || !category?.id) return;

    const categoryId = category?.id;

    // Find the category in searchCategories to get all available item IDs
    const categoryData = searchCategories?.secondary.find(cat => cat.id === categoryId);
    if (!categoryData || !categoryData.items) return;


    // Get all possible item IDs for this category
    const allExistingIdsFromTheCategory = categoryData.items.map(item => item.id);

    setSelectedCategories((prev) => {
      // Check if this category already exists in selectedCategories
      const existingCategoryIndex = prev.secondary?.findIndex(cat => cat.id === categoryId) ?? -1;

      if (existingCategoryIndex === -1) {
        // Category doesn't exist, add it with all IDs selected
        return {
          ...prev,
          secondary: [
            ...(prev.secondary || []),
            {
              id: categoryId,
              selectedItemIds: allExistingIdsFromTheCategory
            }
          ]
        };
      } else {
        // Category exists, check if all items are already selected
        const currentCategory = prev.secondary[existingCategoryIndex];
        const areAllSelected = allExistingIdsFromTheCategory.every(id => 
          currentCategory.selectedItemIds.includes(id)
        );

        const updatedSecondary = [...prev.secondary];
        
        if (areAllSelected) {
          // All are selected, unselect all (clear the array)
          updatedSecondary[existingCategoryIndex] = {
            ...currentCategory,
            selectedItemIds: []
          };
        } else {
          // Not all are selected, select all
          updatedSecondary[existingCategoryIndex] = {
            ...currentCategory,
            selectedItemIds: allExistingIdsFromTheCategory
          };
        }

        return { ...prev, secondary: updatedSecondary };
      }
    });
    
    setCurrentPage(1); // Reset to first page when changing category
  }, [searchCategories]);

  const resetSelectedFilters = useCallback((secondaryOnly = false) => {
    if (secondaryOnly)
      setSelectedCategories({ ...selectedCategories, secondary: [] });
    else
      setSelectedCategories({ primary: null, secondary: [] });

    setCurrentPage(1); // Reset to first page when clearing filters
  }, []);

  // Effects
  // Store customization object when profile is available
  useEffect(() => {
    if (userProfile && templateDynamicInfo !== null && tokenPayload && !isConfigured) {
      storeCustomizationObject(tokenPayload, userProfile, templateDynamicInfo);
    }
  }, [userProfile, templateDynamicInfo, tokenPayload, isConfigured, storeCustomizationObject]);

  // Fetch template dynamic info when token is available
  useEffect(() => {
    // if (userAccessToken) {
    fetchTemplateDynamicInfo();
    // }
  }, [userAccessToken, fetchTemplateDynamicInfo]);

  // Initial data fetching
  useEffect(() => {
    const shouldFetchData = Boolean(
      isConfigured &&
      userCustomizationsHash &&
      userAccessToken
    );

    if (shouldFetchData) {
      configureDesignHuddleLib(userAccessToken, userCustomizationsHash);

      fetchBooks();
      fetchTemplateCategories();
      
      if (isTemplatesPage) {
        fetchTemplates();
      }
      
      fetchProjects();
    }
  }, [
    isConfigured, 
    userCustomizationsHash, 
    userAccessToken, 
    isTemplatesPage,
    fetchBooks,
    fetchTemplateCategories,
    fetchTemplates,
    fetchProjects
  ]);

  // Fetch templates when categories or page changes
  useEffect(() => {
    if (isConfigured && userCustomizationsHash && isTemplatesPage) {
      fetchTemplates(currentPage);
    }
  }, [selectedCategories, currentPage, fetchTemplates, isConfigured, userCustomizationsHash, isTemplatesPage]);

  // Token management
  useEffect(() => {
    const storedToken = getStoredToken();
    if (storedToken) {
      setTokenPayload(storedToken);
      setIsTokenExpired(checkForTokenExpired(storedToken.expiresAt));
    } else {
      refreshToken();
    }
  }, [refreshToken]);

  useEffect(() => {
    if (isTokenExpired) {
      refreshToken();
    }
  }, [isTokenExpired, refreshToken]);

  useEffect(() => {
    if (!tokenPayload) return;

    const expired = checkForTokenExpired(tokenPayload.expiresAt);
    setIsTokenExpired(expired);

    if (!expired) {
      initializeDesignHuddle(tokenPayload);
    }
  }, [tokenPayload, initializeDesignHuddle]);

  // Track usage
  useEffect(() => {
    datadogRum.addAction('templates-builder-usage', { route: location.pathname });
  }, [location.pathname]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Context value
  const contextValue: DesignHuddleContextType = useMemo(() => ({
    // Data
    dhTemplates,
    dhProjects,
    legacyDhProjects,
    bookList,
    userProfile,
    templateDynamicInfo,
    searchCategories,
    selectedCategories,
    
    // Pagination
    currentPage,
    totalPages,
    totalTemplates,
    
    // Auth
    token: tokenPayload?.accessToken || null,
    userCustomizationsHash,
    
    // Loading states
    isLoadingTemplates,
    isLoadingProjects,
    isLoadingDynamicInfo,
    isLoadingBooks,
    
    // Configuration states
    isConfigured,
    isTokenExpired,
    isExportReady,
    templateIsConfigured,
    
    // Setters
    setTemplateIsConfigured,
    setIsExportReady,
    
    // Actions
    refreshToken,
    handleSelectPrimaryCategory,
    handleSelectSecondaryCategory,
    toggleAllSecondaryCategories,
    resetSelectedFilters,
    handlePageChange,
    setSelectedCategories,
  }), [
    dhTemplates,
    dhProjects,
    legacyDhProjects,
    bookList,
    userProfile,
    templateDynamicInfo,
    searchCategories,
    selectedCategories,
    currentPage,
    totalPages,
    totalTemplates,
    tokenPayload,
    userCustomizationsHash,
    isLoadingTemplates,
    isLoadingProjects,
    isLoadingDynamicInfo,
    isLoadingBooks,
    isConfigured,
    isTokenExpired,
    isExportReady,
    templateIsConfigured,
    refreshToken,
    handleSelectPrimaryCategory,
    handleSelectSecondaryCategory,
    toggleAllSecondaryCategories,
    resetSelectedFilters,
    handlePageChange,
    setSelectedCategories,
  ]);

  return (
    <DesignHuddleContext.Provider value={contextValue}>
      {children}
    </DesignHuddleContext.Provider>
  );
};

export const useDesignHuddle = (): DesignHuddleContextType => {
  const context = useContext(DesignHuddleContext);
  if (!context) {
    throw new Error('useDesignHuddle must be used within a DesignHuddleProvider');
  }
  return context;
};